import {Box, IconButton} from '@mui/material';
import {useState} from 'react';
import {Image} from '@jobseeker/lotus/components/Image';
import {PlayButton} from '@jobseeker/lotus/assets/icons';
import {Dialog, VideoPlayer} from '@jobseeker/lotus/components';
import {VersionVariants} from '@jobseeker/lotus/enums';
import {useIsCustomWidth} from '@jobseeker/lotus/hooks';
import {getKeyFromPath, TrackEventUBA} from '@jobseeker/common-core/utils';
import {useRouter} from 'next/router';
import {useSelector} from 'react-redux';
import {AppState} from '@jobseeker/common-core/types';
import type {JobDetail as JB} from '@jobseeker/common-core/stateModels/job/jobdetail.models';

const isAppIimjobs = process.env.NEXT_PUBLIC_APP_NAME === 'iimjobs';

export const JDBrandingBanner = ({
  bannerUrl,
  templateInfo,
  thumbnail,
}: {
  bannerUrl: string;
  templateInfo?: {
    templateNo?: string;
    origin?: string;
  };
  thumbnail?: string;
}) => {
  const [open, setOpen] = useState(false);
  const router = useRouter();
  const jobInfo: JB = useSelector((state: AppState) => state.jobDetail);

  const isVideo =
    /^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.be)\//.test(bannerUrl) ||
    bannerUrl.includes('.mp4');
  const isSmall = useIsCustomWidth('sm');

  const getEmbedUrl = (url: string) => {
    if (url.includes('youtube.com/watch?v=')) {
      const videoId = url.split('v=')[1]?.split('&')[0];
      return `https://www.youtube.com/embed/${videoId}?autoplay=1&controls=1&rel=0`;
    } else if (url.includes('youtu.be/')) {
      const videoId = url.split('youtu.be/')[1]?.split('?')[0];
      return `https://www.youtube.com/embed/${videoId}?autoplay=1&controls=1&rel=0`;
    }
    return url;
  };
  function getYouTubeThumbnail(url: string, quality: string = 'maxresdefault') {
    // Supported qualities: default, mqdefault, hqdefault, sddefault, maxresdefault
    if (thumbnail?.length > 0) {
      return thumbnail;
    }
    const regexList = [
      /v=([^&]+)/, // normal YouTube URL
      /youtu\.be\/([^?]+)/, // short URL
      /embed\/([^?]+)/, // embed URL
      /shorts\/([^?]+)/, // shorts URL
    ];

    let videoId: string | undefined;

    for (const regex of regexList) {
      const match = url.match(regex);
      if (match) {
        videoId = match[1];
        break;
      }
    }

    if (!videoId) {
      return 'https://img.youtube.com/vi/default.jpg';
    }

    return `https://img.youtube.com/vi/${videoId}/${quality}.jpg`;
  }

  return (
    <Box>
      {/* Banner Area */}
      <Box
        sx={{
          overflow: 'hidden',
          position: 'relative',
          zIndex: 0,
          mb: {xxs: 0, md: -5},
          mt: isAppIimjobs ? -2 : 4.25,
          height: {xxs: 105, md: 360},
          ml: {xxs: isAppIimjobs ? -2 : 0, md: 0},
          width: {xxs: isAppIimjobs ? 'calc(100% + 32px)' : '100%', md: '100%'},
          cursor: isVideo ? 'pointer' : 'default',
        }}
        onClick={() => {
          if (isVideo) {
            TrackEventUBA('jdPageClick', 'click', {
              ref: router.query.ref || '',
              pageName: 'Job Description Page',
              jobID: jobInfo?.id,
              jobVersion: '',
              jobRef: getKeyFromPath('ref') || '',
              jobPos: router.query.jobPos || '',
              page: 'JD',
              section: 'Video Banner',
              sessionUsername: sessionStorage.getItem('userId') || '',
              typeClick: 'Video Banner-PlayIcon',
              companyID: jobInfo?.companyData?.companyId,
              centralCompanyID: jobInfo?.recruiter?.centralizedCompanyId,
              companyName: jobInfo?.companyData?.companyName,
              templateID: templateInfo?.templateNo,
              isBranded: 'Yes',
            });
            setOpen(true);
          }
        }}
      >
        {isVideo ? (
          <>
            {bannerUrl.includes('youtube.com') ||
            bannerUrl.includes('youtu.be') ? (
              <Image
                src={getYouTubeThumbnail(bannerUrl)}
                height={isSmall ? 105 : 360}
                width={100}
                alt=""
                style={{
                  width: '100%',
                  height: '100%',
                  display: 'block',
                  objectFit: 'inherit',
                  minHeight: '100px',
                }}
              />
            ) : (
              <>
                {' '}
                <video
                  src={bannerUrl}
                  style={{
                    width: '100%',
                    height: 'auto',
                    display: 'block',
                    objectFit: 'fill',
                    minHeight: '100px',
                  }}
                  muted
                  playsInline
                />
              </>
            )}
            <IconButton
              sx={{
                position: 'absolute',
                top: isAppIimjobs ? '50%' : '55%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                fontSize: 80,
                color: 'white',
              }}
            >
              <Image
                src={PlayButton}
                height={isSmall ? 36 : 48}
                width={isSmall ? 36 : 48}
                alt=""
              />
            </IconButton>
          </>
        ) : (
          <img
            src={bannerUrl}
            alt="Job Branding Banner"
            style={{
              width: '100%',
              display: 'block',
              objectFit: 'fill',
              height: isSmall ? 110 : 360,
            }}
          />
        )}
      </Box>

      <Dialog
        variant={VersionVariants.VERSION_THREE}
        open={open}
        onClose={() => setOpen(false)}
        fullScreen={isSmall ? true : false}
        sx={{
          '& .MuiDialog-paper': {
            minWidth: {xxs: '100%', md: 962},
            height: {xxs: '100%', md: 582},
            borderRadius: {xxs: 0, md: 2},
            backgroundColor: {xxs: 'black', md: 'white'},
          },
          '& .MuiDialogContent-root': {
            px: 0,
            pb: {xxs: 0, md: 0.5},
          },
        }}
        titleProps={{
          mt: {xxs: 6, md: 5},
          ml: 3,
          p: 0,
        }}
        titleBoxProps={{
          borderBottomLeftRadius: isSmall ? 12 : 0,
          borderBottomRightRadius: isSmall ? 12 : 0,
          backgroundColor: 'white',
        }}
      >
        <Box
          sx={{
            width: '100%',
            px: {xxs: 0, md: 1},
            height: {xxs: '100%', md: '99%'},
            bgcolor: {xxs: '#000', md: 'background.paper'},
            borderRadius: {xxs: 0, md: 2},
            overflow: 'hidden',
            boxShadow: 24,
            display: {xxs: 'flex', md: 'block'},
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Box
            sx={
              isSmall
                ? {
                    width: 'calc(100% - 16px)',
                    height: 240,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    p: 0.5,
                    borderRadius: 2,
                    backgroundColor: '#fff',
                  }
                : {}
            }
          >
            {bannerUrl.includes('youtube.com') ||
            bannerUrl.includes('youtu.be') ? (
              <iframe
                src={getEmbedUrl(bannerUrl)}
                width={isSmall ? '100%' : '100%'}
                height={isSmall ? 232 : 530}
                style={{
                  border: 'none',
                  display: 'block',
                  borderRadius: 8,
                }}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            ) : (
              <>
                {' '}
                <VideoPlayer url={bannerUrl} />
              </>
            )}{' '}
          </Box>
        </Box>
      </Dialog>
    </Box>
  );
};
